import { useState } from "react";
import { Archive, Download, ExternalLink, FileText } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ContractDocumentActions } from "@/components/contracts/ContractDocumentActions";

type DocumentKind =
  | "contract_document"
  | "budget"
  | "certification_document"
  | "adjustment_document"
  | "invoice";

interface ContractDocumentRow {
  id: number;
  kind: DocumentKind;
  name: string;
  type: string;
  date: string;
  origin: string | null;
  status: string | null;
  url: string | null;
  reference: string | null;
  installationId?: number;
  seriesId?: number | null;
  versionNumber?: number | null;
  versionKind?: "contract" | "annex" | null;
  uploadedBy?: string | null;
  isContractSigned?: boolean;
  canArchive?: boolean;
  canVersion?: boolean;
  grossCents?: number | string | null;
  invoiceAmount?: number | string | null;
}

interface ArchivedContractDocumentRow {
  id: number;
  seriesId: number | null;
  versionNumber: number | null;
  name: string;
  type: string;
  uploadedAt: string;
  archivedAt: string;
  archivedBy: string;
  origin: string | null;
  auditRecorded: boolean;
}

interface ContractDocumentVersionRow {
  seriesId: number;
  kind: "contract" | "annex";
  documentId: number;
  versionNumber: number;
  isCurrent: boolean;
  name: string;
  uploadedAt: string;
  uploadedBy: string;
  installationId: number;
  origin: string | null;
  downloadUrl: string;
}

interface NewVersionTarget {
  id: number;
  name: string;
  installationId: number;
  seriesId: number | null;
  versionKind: "contract" | "annex" | null;
  isContractSigned?: boolean;
  origin: string | null;
}

const CATEGORY_LABELS: Record<DocumentKind, string> = {
  contract_document: "Contratos y anexos",
  budget: "Presupuesto aceptado",
  adjustment_document: "Anexos y modificaciones",
  certification_document: "Certificaciones",
  invoice: "Facturas",
};

const STATUS_LABELS: Record<string, string> = {
  signed: "Firmado",
  pending: "Pendiente",
  in_progress: "En curso",
  completed: "Completado",
  cancelled: "Cancelado",
  draft: "Borrador",
  pending_pm_review: "Pendiente de revisión",
  pm_approved: "Aprobado por PM",
  sent_to_client: "Enviado al cliente",
  client_accepted: "Aceptado por cliente",
  rejected: "Rechazado",
  review_required: "Revisión pendiente",
  extracted: "Extraído",
  confirmed: "Confirmado",
};

const CATEGORY_ORDER: DocumentKind[] = [
  "contract_document",
  "budget",
  "adjustment_document",
  "certification_document",
  "invoice",
];

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Fecha no disponible";
  return date.toLocaleDateString("es-ES");
}

function formatMoney(value: number, cents = false): string | null {
  const amount = cents ? value / 100 : value;
  if (!Number.isFinite(amount)) return null;
  return amount.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
}

function statusLabel(
  value: string | null,
  isContractSigned?: boolean
): string | null {
  if (isContractSigned) return "Firmado";
  if (!value) return null;
  return STATUS_LABELS[value] ?? "Estado registrado";
}

function rowAmount(row: ContractDocumentRow): string | null {
  if (row.kind === "budget" && row.grossCents != null) {
    return formatMoney(Number(row.grossCents), true);
  }
  if (row.kind === "invoice" && row.invoiceAmount != null) {
    return formatMoney(Number(row.invoiceAmount));
  }
  return null;
}

interface ContractDocumentsPanelProps {
  contractId: number;
  // Capacidades ya autoritativas de customers.contracts.getMyCapabilities.
  // Fail-closed: undefined/false mantiene el panel en modo solo lectura.
  canWriteCommercial?: boolean;
  canWriteEconomic?: boolean;
  canArchiveDocuments?: boolean;
}

export function ContractDocumentsPanel({
  contractId,
  canWriteCommercial = false,
  canWriteEconomic = false,
  canArchiveDocuments = false,
}: ContractDocumentsPanelProps) {
  const utils = trpc.useUtils();
  const [archiveTarget, setArchiveTarget] =
    useState<ContractDocumentRow | null>(null);
  const [versionTarget, setVersionTarget] = useState<NewVersionTarget | null>(null);
  const query = trpc.customers.contracts.listDocuments.useQuery(
    { contractId },
    { enabled: !!contractId }
  );
  const archivedQuery = trpc.customers.contracts.listArchivedDocuments.useQuery(
    { contractId },
    { enabled: !!contractId }
  );
  const versionsQuery = trpc.customers.contracts.listDocumentVersions.useQuery(
    { contractId },
    { enabled: !!contractId }
  );
  const archiveMutation = trpc.documents.delete.useMutation();

  const showActions =
    contractId > 0 && (canWriteCommercial || canWriteEconomic);

  return (
    <div className="space-y-4">
      {showActions && (
        <ContractDocumentActions
          contractId={contractId}
          canWriteCommercial={canWriteCommercial}
          canWriteEconomic={canWriteEconomic}
          versionTarget={versionTarget}
          onVersionTargetHandled={() => setVersionTarget(null)}
        />
      )}
      {query.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No se pudieron cargar los documentos del contrato.
          </CardContent>
        </Card>
      ) : (
        <DocumentsList
          documents={(query.data ?? []) as ContractDocumentRow[]}
          canArchiveDocuments={canArchiveDocuments}
          onArchive={setArchiveTarget}
          canWriteCommercial={canWriteCommercial}
          onNewVersion={document => setVersionTarget({
            id: document.id,
            name: document.name,
            installationId: Number(document.installationId),
            seriesId: document.seriesId ?? null,
            versionKind: document.versionKind ?? null,
            isContractSigned: document.isContractSigned,
            origin: document.origin,
          })}
        />
      )}
      <VersionHistoryList
        documents={(versionsQuery.data ?? []) as ContractDocumentVersionRow[]}
        isLoading={versionsQuery.isLoading}
        isError={versionsQuery.isError}
      />
      <ArchivedDocumentsList
        documents={(archivedQuery.data ?? []) as ArchivedContractDocumentRow[]}
        isLoading={archivedQuery.isLoading}
        isError={archivedQuery.isError}
      />
      <AlertDialog
        open={archiveTarget !== null}
        onOpenChange={open => {
          if (!open && !archiveMutation.isPending) setArchiveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archivar documento contractual</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveTarget ? (
                archiveTarget.seriesId != null ? (
                  <>
                    «{archiveTarget.name}» archivará el documento lógico completo
                    y todas las versiones de su serie. Dejarán de aparecer en
                    lecturas operativas; los registros, relaciones y archivos
                    almacenados se conservarán y se auditará cada versión.
                  </>
                ) : (
                  <>
                    «{archiveTarget.name}» dejará de aparecer como documento
                    operativo del contrato y de la instalación. Se conservarán
                    el registro y el archivo almacenado, y el historial anotará
                    quién y cuándo lo archivó.
                  </>
                )
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMutation.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!archiveTarget || archiveMutation.isPending}
              onClick={event => {
                event.preventDefault();
                void (async () => {
                  if (!archiveTarget) return;
                  try {
                    await archiveMutation.mutateAsync({ id: archiveTarget.id });
                    await Promise.all([
                      utils.customers.contracts.listDocuments.invalidate({
                        contractId,
                      }),
                      utils.customers.contracts.listArchivedDocuments.invalidate(
                        { contractId }
                      ),
                      utils.customers.contracts.listDocumentVersions.invalidate({
                        contractId,
                      }),
                    ]);
                    toast.success("Documento archivado correctamente");
                    setArchiveTarget(null);
                  } catch (error) {
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : "No se pudo archivar el documento"
                    );
                  }
                })();
              }}
            >
              {archiveMutation.isPending ? "Archivando…" : "Archivar documento"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DocumentsList({
  documents,
  canArchiveDocuments,
  onArchive,
  canWriteCommercial,
  onNewVersion,
}: {
  documents: ContractDocumentRow[];
  canArchiveDocuments: boolean;
  onArchive: (document: ContractDocumentRow) => void;
  canWriteCommercial: boolean;
  onNewVersion: (document: ContractDocumentRow) => void;
}) {
  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No hay documentos operativos asociados a este contrato.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {CATEGORY_ORDER.map(kind => {
        const categoryDocuments = documents.filter(
          document => document.kind === kind
        );
        if (categoryDocuments.length === 0) return null;

        return (
          <Card key={kind}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {CATEGORY_LABELS[kind]}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {categoryDocuments.map(document => {
                const amount = rowAmount(document);
                const forceDownload = ["budget", "invoice", "contract_document"].includes(document.kind);
                const context = statusLabel(
                  document.status,
                  document.isContractSigned
                );
                return (
                  <div
                    key={`${document.kind}:${document.id}`}
                    className="flex flex-wrap items-center gap-3 rounded-md border p-3"
                  >
                    <FileText
                      className="h-5 w-5 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-medium">
                          {document.name}
                        </span>
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          {CATEGORY_LABELS[document.kind]}
                        </Badge>
                        {document.versionNumber != null ? (
                          <Badge variant="outline" className="shrink-0 text-xs">
                            Versión {document.versionNumber} · Vigente
                          </Badge>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span>{document.type}</span>
                        <span>{formatDate(document.date)}</span>
                        {document.origin ? (
                          <span>{document.origin}</span>
                        ) : null}
                        {context ? <span>{context}</span> : null}
                        {document.reference ? (
                          <span>Ref. {document.reference}</span>
                        ) : null}
                        {amount ? <span>{amount}</span> : null}
                      </div>
                    </div>
                    {document.url ? (
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                      >
                        <a
                          href={document.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {forceDownload ? (
                            <Download
                              className="mr-1 h-4 w-4"
                              aria-hidden="true"
                            />
                          ) : (
                            <ExternalLink
                              className="mr-1 h-4 w-4"
                              aria-hidden="true"
                            />
                          )}
                          {forceDownload ? "Descargar" : "Abrir"}
                        </a>
                      </Button>
                    ) : null}
                    {canArchiveDocuments &&
                    document.kind === "contract_document" &&
                    document.canArchive ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        aria-label={`Archivar ${document.name}`}
                        onClick={() => onArchive(document)}
                      >
                        <Archive className="mr-1 h-4 w-4" aria-hidden="true" />
                        Archivar
                      </Button>
                    ) : null}
                    {canWriteCommercial &&
                    document.kind === "contract_document" &&
                    document.canVersion &&
                    document.installationId ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        aria-label={`Nueva versión de ${document.name}`}
                        onClick={() => onNewVersion(document)}
                      >
                        Nueva versión
                      </Button>
                    ) : null}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function VersionHistoryList({
  documents,
  isLoading,
  isError,
}: {
  documents: ContractDocumentVersionRow[];
  isLoading: boolean;
  isError: boolean;
}) {
  const grouped = new Map<number, ContractDocumentVersionRow[]>();
  for (const document of documents) {
    const existing = grouped.get(document.seriesId) ?? [];
    existing.push(document);
    grouped.set(document.seriesId, existing);
  }

  return (
    <details className="rounded-md border p-4">
      <summary className="cursor-pointer text-sm font-medium">
        Historial de versiones
      </summary>
      <div className="mt-3 space-y-3">
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            No se pudo cargar el historial de versiones.
          </p>
        ) : grouped.size === 0 ? (
          <p className="text-sm text-muted-foreground">
            Todavía no hay series versionadas. Los documentos históricos sin
            vincular permanecen como documentos independientes.
          </p>
        ) : Array.from(grouped.entries()).map(([seriesId, versions]) => (
          <section key={seriesId} className="space-y-2 rounded-md border p-3">
            <h3 className="text-sm font-medium">
              {versions[0].kind === "contract" ? "Contrato" : "Anexo contractual"}
              {versions[0].origin ? ` · ${versions[0].origin}` : ""}
            </h3>
            {versions.map(version => (
              <div
                key={version.documentId}
                className="flex flex-wrap items-center gap-3 rounded-md border p-3"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium">
                      Versión {version.versionNumber} · {version.name}
                    </span>
                    <Badge variant={version.isCurrent ? "secondary" : "outline"}>
                      {version.isCurrent ? "Vigente" : "Versión anterior"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(version.uploadedAt)} · {version.uploadedBy}
                  </p>
                </div>
                <Button asChild size="sm" variant="outline" className="shrink-0">
                  <a href={version.downloadUrl}>
                    <Download className="mr-1 h-4 w-4" aria-hidden="true" />
                    Descargar
                  </a>
                </Button>
              </div>
            ))}
          </section>
        ))}
      </div>
    </details>
  );
}

function ArchivedDocumentsList({
  documents,
  isLoading,
  isError,
}: {
  documents: ArchivedContractDocumentRow[];
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Historial de documentos archivados
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            No se pudo cargar el historial de documentos archivados.
          </p>
        ) : documents.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No hay documentos archivados.
          </p>
        ) : (
          documents.map(document => (
            <div
              key={document.id}
              className="flex flex-wrap items-start gap-3 rounded-md border border-dashed p-3"
            >
              <FileText
                className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {document.name}
                  </span>
                  <Badge variant="outline" className="shrink-0 text-xs">
                    Documento archivado
                  </Badge>
                  {document.versionNumber != null ? (
                    <Badge variant="outline" className="shrink-0 text-xs">
                      Versión {document.versionNumber}
                    </Badge>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>{document.type}</span>
                  <span>Subido {formatDate(document.uploadedAt)}</span>
                  <span>
                    Archivado {formatDate(document.archivedAt)} por{" "}
                    {document.archivedBy}
                  </span>
                  {document.origin ? <span>{document.origin}</span> : null}
                </div>
                {!document.auditRecorded ? (
                  <p className="text-xs text-destructive">
                    No se encontró el evento de auditoría asociado.
                  </p>
                ) : null}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
